



































import { sanitize } from 'dompurify';
import Vue from 'vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import VueRouter, { Route } from 'vue-router';
import { namespace } from 'vuex-class';

const SearchSuggesterResults = () =>
	import(
		/* webpackChunkName: "search-suggester-results" */ '@/components/search/SearchSuggesterResults/SearchSuggesterResults.vue'
	);

import SearchResultItem from '@/components/search/SearchSuggesterResults/SearchResults/SearchResultItem.vue';
import SearchPoster from '@/components/search/SearchSuggesterResults/SearchSuggesterEmpty/SearchPoster.vue';
import SearchTermOld from '@/components/search/SearchSuggesterResults/SearchSuggesterEmpty/SearchTermOld.vue';

import { CollectionType } from '@/enums/collection-type';
import { Viewport } from '@/enums/viewport';
import { WebLocale } from '@/enums/web-locale';
import { providerPageExpTrackEvent } from '@/helpers/experiments/popular-provider-page-exp-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

const Language = namespace('language');
const SearchSuggester = namespace('searchSuggester');
const Ui = namespace('ui');
const Routing = namespace('routing');

/**
 * @deprecated Should be replaced with new search.
 */
@Component({
	name: 'SearchSuggesterBar',
	components: {
		SearchSuggesterResults,
	},
})
export default class SearchSuggesterBar extends Vue {
	isMounted = false;
	items: Vue[] = [];
	activeElement: SearchTermOld | SearchPoster | SearchResultItem;
	index = -1;
	previndex = -1;

	private inputQuery = '';

	@Prop() collectionType: CollectionType;

	@Ref('search-result') readonly searchResult?: Vue;

	@Language.State webLocale: WebLocale;

	@SearchSuggester.Getter isShown: boolean;
	@SearchSuggester.Getter query: string;
	@SearchSuggester.Getter currentView: string;
	@SearchSuggester.Action search: ({ searchQuery, router }: { searchQuery: string; router: VueRouter }) => void;
	@SearchSuggester.Action reset: () => void;
	@SearchSuggester.Action goToSearch: ({
		searchString,
		personId,
	}: {
		searchString: string;
		personId?: number;
		source?: string;
	}) => void;
	@SearchSuggester.Action setShown: (value: boolean) => void;
	@SearchSuggester.Mutation SET_LOADING: (value: boolean) => void;

	@Ui.State viewport: Viewport;

	@Routing.State activeRoute: any;

	get debounceValue() {
		// web: 300
		return 300;
	}

	get placeholder() {
		return this.viewport === Viewport.DESKTOP ? this.$t('WEBAPP_SEARCH_PLACEHOLDER') : this.$t('WEBAPP_SEARCH');
	}

	get showCancelButtonMobileWebApp() {
		return this.viewport === Viewport.SMARTPHONE && this.query?.length > 0 && this.isShown;
	}

	get showCancelButton() {
		const forMobile = this.collectionType === CollectionType.SEARCH ? 'never' : 'always';
		return this.showCancelButtonMobileWebApp ? forMobile : 'never';
	}

	@Watch('collectionType')
	onCollectionTypeChange() {
		if (this.collectionType !== CollectionType.SEARCH) {
			this.$emit('ionCancel');
		}
	}

	@Watch('currentView')
	onViewChange() {
		this.resetItems();
	}

	@Watch('query')
	onQueryChange() {
		if (!this.isShown && !this.inputQuery) {
			this.inputQuery = this.query;
		}
	}

	/**
	 * close results when the route has been changed.
	 */
	@Watch('activeRoute')
	onRouteChange(route: Route) {
		this.cancel();

		// if the route has changed to a persons page, replace the input with the proper name of the person
		if (route.query.person_id) {
			(this.$refs.searchbar as HTMLIonSearchbarElement).value = route.query.q as string;
		}
	}

	async mounted() {
		this.isMounted = true;
	}

	cancel() {
		// if user is on search, we want to keep the search bar
		if (this.collectionType !== CollectionType.SEARCH) {
			this.$emit('ionCancel');
			this.setShown(false);
		}
	}

	onBlur() {
		// We do a small timeout, so that, if search item is clicked - it doesn't close before click happens.
		setTimeout(() => {
			this.$emit('blur');
		}, 10);
	}

	onChange(evt: any) {
		const userInput = sanitize(evt.target.value);

		this.inputQuery = userInput;
		this.search({ searchQuery: this.inputQuery, router: this.$router });
	}

	async onKeyUp(keyCode: number, searchQuery: string) {
		// I'm using keys that are not a strings, so I set a Map structure
		// instead of an object
		const keyMapping = new Map([
			[
				13, // Enter
				async () => {
					this.onSearch(searchQuery);
					// blur the input field
					const input = await this.getInputElement();
					input.blur();
				},
			],
		]);
		if (keyMapping.has(keyCode)) {
			const specialBehavior = keyMapping.get(keyCode);
			specialBehavior!();
		} else {
			this.setShown(true);
		}
	}

	openSearchbar(evt: any) {
		if (!this.isShown) {
			const userInput = sanitize(evt.target.value);

			this.search({ searchQuery: userInput, router: this.$router });
			this.setShown(true);
		}
	}

	private async getInputElement() {
		const searchbar = (await this.getSearchbar()) as HTMLIonSearchbarElement;
		return await searchbar.getInputElement();
	}

	async focuBtn(searchTerm?: string) {
		const input = await this.getSearchbar();
		await input.setFocus();
		input.value = searchTerm;
	}

	private async getSearchbar() {
		const searchbar = this.$refs.searchbar as HTMLIonSearchbarElement;
		return searchbar;
	}

	navigateElements(keyCode: number) {
		this.loadElements();
		if (this.activeElement === null && this.items.length === 0) {
			return;
		} else {
			switch (keyCode) {
				case 39: // up
				case 40: //right
					{
						if (this.items.length > 0 && this.index + 1 <= this.items.length - 1) {
							this.previndex = this.index;
							this.index++;
						} else {
							this.previndex = this.index;
							this.index = 0;
						}

						this.move();
					}

					break;
				case 37: //down
				case 38: //left
					{
						if (this.index - 1 >= 0) {
							this.previndex = this.index;
							this.index--;
						} else {
							this.previndex = this.index;
							this.index = this.items.length - 1;
						}

						this.move();
					}

					break;
				case 27: //escape
					this.clear('escape');
					this.resetItems();
					break;
				case 13: //enter
					if (this.activeElement && this.activeElement.focused) {
						this.activeElement.clicked();
					} else if (this.query?.length >= 2 && this.index === -1 && this.previndex === -1) {
						this.onSearch(this.query);
					}
					this.resetItems();
					this.activeElement?.unfocus();
					break;
				default:
					return;
			}
		}
	}

	private loadElements(): void {
		const { searchResult } = this;
		const elements = searchResult?.$children[0]?.$children || [];
		this.items = elements;
	}

	private focusElement(index: number) {
		const activeElement = this.getElement(this.items[index]);
		activeElement?.focus();
	}

	private unfocusElement(index: number) {
		const activeElement = this.getElement(this.items[index]);
		if (activeElement?.focused) {
			activeElement.unfocus();
		}
	}

	private move() {
		if (this.previndex >= 0) {
			this.unfocusElement(this.previndex);
		}

		this.focusElement(this.index);
		this.activeElement = this.getElement(this.items[this.index]);
	}

	private getElement(element: Vue): SearchTermOld | SearchPoster | SearchResultItem {
		const elementName: string | undefined =
			element.$vnode.componentOptions != undefined ? element.$vnode.componentOptions.tag : '';
		switch (elementName) {
			case 'SearchTermOld':
				return element as SearchTermOld;
			case 'SearchPoster':
				return element as SearchPoster;
			case 'SearchResultItem':
				return element as SearchResultItem;
			default:
				return element as SearchTermOld;
		}
	}

	private onSearch(searchQuery: string) {
		this.setShown(false);
		this.goToSearch({ searchString: searchQuery });
		TrackingHelper.trackEvent('search_suggest_more', {
			action: 'enter',
			label: searchQuery,
		});
	}

	private resetItems() {
		this.index = -1;
		this.previndex = -1;
	}

	clear(source: string) {
		TrackingHelper.trackEvent('search_suggest_clear', {
			action: source,
		});
		providerPageExpTrackEvent({
			label: 'search_suggest_clear',
			action: 'click',
		});
		this.reset();
	}

	async onEnter() {
		// blur the input field
		const input = await this.getInputElement();
		input.blur();
	}
}
